import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import PublicIntakeApi, {
  IntakeCourtDto,
  IntakeFormData,
  IntakeTierDto,
} from '../../api/PublicIntakeApi';
// Faces are imported here rather than from the stylesheet — Sass cannot @use "400.css". Archivo
// carries the page, IBM Plex Mono carries anything that is data on the client's citation.
import '@fontsource/archivo/400.css';
import '@fontsource/archivo/500.css';
import '@fontsource/archivo/600.css';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';
import './IntakeView.scss';

/**
 * Public intake — answering a citation and paying for representation in one sitting.
 *
 * The visual language is taken from the citation itself: boxed fields, small capital labels, and
 * typewritten data. That is not decoration. The client is holding a form and this page is the
 * document that answers it, so the two should look related.
 *
 * The one moment the design is built around is the fee. Under Lloyd's Aug 6 pricing decision the
 * Best plan costs $600 in most courts and up to $4,900 in twelve named ones, so the amount is not
 * knowable until the client says where the case is. A client must never read $600 and be charged
 * $2,900. The fee panel therefore starts as an unfilled field and resolves, visibly and with the
 * reason stated, the moment a court is chosen.
 *
 * No personal data appears in the URL, the page title or anything an analytics script can read.
 * The path carries the plan and nothing else.
 */

const PRODUCT_LABELS: Record<string, string> = {
  'non-cdl': 'Non-commercial traffic citation',
  cdl: 'Commercial (CDL) traffic citation',
};

const PRODUCT_CODES: Record<string, string> = {
  'non-cdl': 'NON_CDL',
  cdl: 'CDL',
};

const TIER_CODES: Record<string, 'GOOD' | 'BETTER' | 'BEST'> = {
  good: 'GOOD',
  better: 'BETTER',
  best: 'BEST',
};

const EMPTY_FORM: IntakeFormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  dateOfBirth: '',
  citationNumber: '',
  offenseDescription: '',
  city: '',
  county: '',
};

const PRIVACY_POLICY_URL = 'https://txticketdr.com/privacy-policy/';
const OFFICE_PHONE = '210-210-1616';

interface IntakeViewProps {
  /**
   * Which product this route sells. A PROP, not a param, and that distinction is the whole point.
   *
   * The routes are `/non-cdl/:tier` and `/cdl/:tier` — `cdl` is a literal path segment, not a
   * `:product` placeholder, so useParams() never returned a product and the old
   * `const { product = 'non-cdl' } = useParams()` fell through to its default on EVERY route. It
   * looked right only because the default happened to match one of the two.
   *
   * The consequence was live: /cdl/good rendered the non-commercial page, at the non-commercial
   * $375, with non-commercial plan copy, to a client who had followed a CDL link. CDL is
   * deliberately not sold online at all — footnote 2 on the CDL page excludes courts of record and
   * nobody has built that eligibility check — so this was the one product that must never be
   * purchasable, quietly purchasable at the wrong price.
   */
  product: 'non-cdl' | 'cdl';
}

const IntakeView = ({ product }: IntakeViewProps) => {
  const { tier = 'good' } = useParams();

  const productCode = PRODUCT_CODES[product.toLowerCase()];
  const tierCode = TIER_CODES[tier.toLowerCase()];

  const [tiers, setTiers] = useState<IntakeTierDto[] | null>(null);
  const [courts, setCourts] = useState<IntakeCourtDto[]>([]);
  const [courtGuid, setCourtGuid] = useState<string>('');
  const [form, setForm] = useState<IntakeFormData>(EMPTY_FORM);
  const [quote, setQuote] = useState<{ amount: string; reason: string } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const plan = useMemo(
    () => tiers?.find((t) => t.tier === tierCode) ?? null,
    [tiers, tierCode],
  );

  useEffect(() => {
    if (!productCode || !tierCode) {
      setLoadError('That plan link is not one we recognise.');
      return;
    }
    Promise.all([
      PublicIntakeApi.getPricing(productCode),
      PublicIntakeApi.getCourts(),
    ])
      .then(([pricing, courtList]) => {
        setTiers(pricing.tiers);
        setCourts(courtList);
      })
      .catch((error: Error) => setLoadError(error.message));
  }, [productCode, tierCode]);

  const set = (field: keyof IntakeFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const selectedCourt = courts.find((c) => c.guid === courtGuid) ?? null;

  /**
   * Re-price whenever the court or the date of birth changes — the two things that move the number.
   *
   * The client sees the real fee here, not at Stripe. Someone filing in Lubbock on the Best plan
   * pays $4,900 rather than $600, and finding that out on a payment screen would be indefensible.
   */
  useEffect(() => {
    if (!productCode || !tierCode) return;
    if (!courtGuid && !form.dateOfBirth) {
      setQuote(null);
      return;
    }

    let current = true;
    PublicIntakeApi.getQuote({
      product: productCode,
      tier: tierCode,
      courtGuid: courtGuid || null,
      dateOfBirth: form.dateOfBirth || null,
    })
      .then((result) => {
        if (!current) return;
        setQuote(
          result.quotable
            ? { amount: result.amountFormatted ?? '', reason: result.explanation }
            : null,
        );
        // A missing court is not an error, it is an unfinished form — the fee panel already says so.
        // Only speak up once they have chosen one and we still cannot price it.
        setSubmitError(!result.quotable && courtGuid ? result.explanation : null);
      })
      .catch(() => {
        // A failed quote must not look like a price. Fall back to the unresolved state.
        if (current) setQuote(null);
      });

    return () => {
      current = false;
    };
  }, [productCode, tierCode, courtGuid, form.dateOfBirth]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError(null);
    setSubmitting(true);

    try {
      const session = await PublicIntakeApi.createSession({
        product: productCode,
        tier: tierCode,
        courtGuid: courtGuid || null,
        formData: form,
      });

      if (!session.quotable) {
        // Not a failure. This case needs a person, and saying so plainly beats a dead end.
        setQuote(null);
        setSubmitError(session.explanation);
        setSubmitting(false);
        return;
      }

      // The server priced it again when it held the form. If that differs from the figure on screen,
      // stop: show the new one and make them press again. Nobody reaches a card form having agreed
      // to a different number than the one they are about to pay.
      const confirmed = { amount: session.amountFormatted ?? '', reason: session.explanation };
      if (quote && quote.amount !== confirmed.amount) {
        setQuote(confirmed);
        setSubmitError(
          `The fee for this case is ${confirmed.amount}. Check it and continue when you are ready.`,
        );
        setSubmitting(false);
        return;
      }
      setQuote(confirmed);

      const checkoutUrl = await PublicIntakeApi.startCheckout(session.sessionGuid);
      window.location.href = checkoutUrl;
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Something went wrong.');
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <div className="intake">
        <div className="intake__notice">
          <p className="intake__notice-title">{loadError}</p>
          <p>
            Call the office on <a href={`tel:${OFFICE_PHONE}`}>{OFFICE_PHONE}</a> and we will take
            the details over the phone.
          </p>
        </div>
      </div>
    );
  }

  const priceIsCourtDependent = plan?.courtDependent ?? false;
  const listPrice = plan?.fromPriceFormatted ?? null;

  return (
    <div className="intake">
      <Helmet>
        {/* Plan only. No name, email or citation — this title is read by analytics. */}
        <title>Representation request — {tierCode.toLowerCase()} plan</title>
      </Helmet>

      <header className="intake__bar">
        <span className="intake__mark">Texas Ticket Doctor</span>
        <a className="intake__phone" href={`tel:${OFFICE_PHONE}`}>
          {OFFICE_PHONE}
        </a>
      </header>

      <main className="intake__sheet">
        <div className="intake__masthead">
          <p className="intake__eyebrow">Representation request</p>
          <h1 className="intake__title">{PRODUCT_LABELS[product.toLowerCase()]}</h1>
          <span className="intake__stamp">{tierCode}</span>
        </div>

        {/* The fee. Deliberately the first thing after the heading, and deliberately shown as
            unresolved until it can be stated truthfully. */}
        <section
          className={`intake__fee ${quote ? 'intake__fee--resolved' : ''}`}
          aria-live="polite"
        >
          <p className="intake__field-label">Fee</p>
          {quote ? (
            <>
              <p className="intake__amount">{quote.amount}</p>
              <p className="intake__amount-note">{quote.reason}</p>
            </>
          ) : (
            <>
              <p className="intake__amount">
                {priceIsCourtDependent && listPrice ? `from ${listPrice}` : listPrice ?? '—'}
              </p>
              <p className="intake__amount-note">
                {priceIsCourtDependent
                  ? 'This plan costs more in some courts. Choose your court below and the exact fee appears here before you pay.'
                  : 'This is the full fee for this plan.'}
              </p>
            </>
          )}
        </section>

        {plan && plan.copy.length > 0 && (
          <section className="intake__includes">
            <p className="intake__field-label">What this plan covers</p>
            <ul>
              {plan.copy.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </section>
        )}

        <form className="intake__form" onSubmit={handleSubmit}>
          <fieldset className="intake__group">
            <legend className="intake__field-label">Your details</legend>
            <div className="intake__row">
              <Field label="First name" value={form.firstName} onChange={set('firstName')} required />
              <Field label="Last name" value={form.lastName} onChange={set('lastName')} required />
            </div>
            <div className="intake__row">
              <Field label="Email" type="email" value={form.email} onChange={set('email')} required />
              <Field label="Phone" type="tel" value={form.phone} onChange={set('phone')} required />
            </div>
            <div className="intake__row">
              <Field
                label="Date of birth"
                type="date"
                value={form.dateOfBirth}
                onChange={set('dateOfBirth')}
                hint="Citations for anyone 17 or under are billed at a flat $850."
                required
              />
            </div>
          </fieldset>

          <fieldset className="intake__group">
            <legend className="intake__field-label">Your citation</legend>
            <div className="intake__row">
              <Field
                label="Citation number"
                value={form.citationNumber}
                onChange={set('citationNumber')}
                mono
              />
              <Field label="Offense as written" value={form.offenseDescription} onChange={set('offenseDescription')} />
            </div>
            <div className="intake__row">
              <label className="intake__field intake__field--wide">
                <span className="intake__field-label">Court handling your case</span>
                <select
                  className="intake__input intake__input--mono"
                  value={courtGuid}
                  onChange={(event) => setCourtGuid(event.target.value)}
                  required
                >
                  <option value="">Select your court</option>
                  {courts.map((court) => (
                    <option key={court.guid} value={court.guid}>
                      {court.name}
                      {court.county ? ` — ${court.county}` : ''}
                    </option>
                  ))}
                </select>
                <span className="intake__hint">
                  It is printed on your citation. We check what you choose against the citation and
                  raise it with you if the two disagree.
                </span>
              </label>
            </div>
            {selectedCourt && (
              <p className="intake__confirm">
                Filing against <strong>{selectedCourt.name}</strong>
              </p>
            )}
          </fieldset>

          {submitError && (
            <div className="intake__error" role="alert">
              <p>{submitError}</p>
              <p>
                Call <a href={`tel:${OFFICE_PHONE}`}>{OFFICE_PHONE}</a> and we will handle it
                directly.
              </p>
            </div>
          )}

          <button className="intake__submit" type="submit" disabled={submitting}>
            {submitting ? 'Working…' : 'Continue to payment'}
          </button>

          <p className="intake__legal">
            Card details are handled by Stripe. We never see or store them. Read our{' '}
            <a href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer">
              privacy policy
            </a>{' '}
            before you submit.
          </p>
        </form>
      </main>
    </div>
  );
};

interface FieldProps {
  label: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  hint?: string;
  mono?: boolean;
  required?: boolean;
}

const Field = ({ label, value, onChange, type = 'text', hint, mono, required }: FieldProps) => (
  <label className="intake__field">
    <span className="intake__field-label">{label}</span>
    <input
      className={`intake__input ${mono ? 'intake__input--mono' : ''}`}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
    />
    {hint && <span className="intake__hint">{hint}</span>}
  </label>
);

export default IntakeView;
